import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import LocalizedLink from "@/components/LocalizedLink";
import LanguageSelector from "@/components/LanguageSelector";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useTemplateStore, useAuthStore } from "@/store";
import {
  LANGUAGE_NAMES,
  SUPPORTED_LANGUAGES,
  getPathLanguage,
  stripLanguagePrefix,
  withLanguagePrefix,
} from "@/lib/i18n";
import { useLocalizedNavigation } from "@/hooks/useLocalizedNavigation";
import {
  Play,
  Search,
  Filter,
  Grid3X3,
  LayoutList,
  Sparkles,
  Clock,
  Menu,
  X,
  ChevronDown,
  ChevronUp,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

const Navbar = () => {
  const { isAuthenticated, user } = useAuthStore();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 glass border-b border-slate-200/50">
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex items-center justify-between h-16">
          <LocalizedLink to="/" className="flex items-center gap-2">
            <img
              src="/assets/logo-creavideos.png"
              alt="CreaVideos"
              className="h-8 w-auto"
            />
          </LocalizedLink>

          <div className="hidden md:flex items-center gap-8">
            <LocalizedLink to="/templates" className="text-teal-600 font-medium">
              Templates
            </LocalizedLink>
            <LocalizedLink to="/pricing" className="text-slate-600 hover:text-teal-600 transition-colors font-medium">
              Pricing
            </LocalizedLink>
            <LanguageSelector />
            {isAuthenticated ? (
              <LocalizedLink to={user?.role === "admin" ? "/admin" : "/dashboard"}>
                <Button className="btn-primary rounded-full px-6">Dashboard</Button>
              </LocalizedLink>
            ) : (
              <div className="flex items-center gap-3">
                <LocalizedLink to="/login">
                  <Button variant="ghost" className="btn-ghost">Log in</Button>
                </LocalizedLink>
              </div>
            )}
          </div>

          <div className="flex items-center md:hidden">
            <LanguageSelector />
            <button className="p-2" onClick={() => setMobileMenuOpen(!mobileMenuOpen)} aria-label="Toggle navigation menu">
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </button>
          </div>
        </div>

        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-slate-200">
            <div className="flex flex-col gap-4">
              <LocalizedLink to="/templates" className="text-teal-600 font-medium py-2">
                Templates
              </LocalizedLink>
              <LocalizedLink to="/pricing" className="text-slate-600 font-medium py-2">
                Pricing
              </LocalizedLink>
              {isAuthenticated ? (
                <LocalizedLink to={user?.role === "admin" ? "/admin" : "/dashboard"}>
                  <Button className="btn-primary rounded-full w-full">Dashboard</Button>
                </LocalizedLink>
              ) : (
                <LocalizedLink to="/login">
                  <Button variant="outline" className="w-full">Log in</Button>
                </LocalizedLink>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

const TEMPLATE_CATEGORY_TRANSLATIONS = {
  en: {
    all: "Category: All",
    social: "Transform Image to video with AI",
    product: "Simple & Visual video templates",
    edited: "Simple edited video templates",
    marketing: "Fun & Creative video templates",
  },
  fr: {
    all: "Catégorie : Tous",
    social: "Transformer une image en vidéo avec l’IA",
    product: "Modèles vidéo simples et visuels",
    edited: "Modèles vidéo montés simples",
    marketing: "Modèles vidéo amusants et créatifs",
  },
  es: {
    all: "Categoría: Todo",
    social: "Transformar imagen en video con IA",
    product: "Plantillas de video simples y visuales",
    edited: "Plantillas de video editado simples",
    marketing: "Plantillas de video divertidas y creativas",
  },
};

const CATEGORY_ORDER = ["social", "product", "edited", "marketing"];

const CATEGORY_DESCRIPTIONS = {
  en: {
    social: "Turn any static image into a ready-to-post video. These templates already include optimized AI prompts, so you just upload your image, click “Generate Video,” then edit or remove the text before exporting and sharing.",
    product: "Instead of posting an image, turn it into an engaging video post. Whether it’s a single photo or a 4-image slideshow, these templates add smooth animations and text to help your posts reach more people.",
    edited: "Already filmed your clips but don’t want to spend time editing, or don’t know how? Upload your clips into one of these templates, choose the number and duration of clips you need, then export and share the finished video to all your platforms in one click.",
    marketing: "Grab attention with short, humorous videos featuring an actor in everyday situations. Perfect for ads or posts that feel like mini-stories and make your audience smile.",
  },
  fr: {
    social: "Transformez n’importe quelle image statique en vidéo prête à publier. Ces modèles incluent déjà des prompts IA optimisés : importez votre image, cliquez sur « Générer la vidéo », puis modifiez ou supprimez le texte avant d’exporter et de partager.",
    product: "Au lieu de publier une image, transformez-la en publication vidéo engageante. Qu’il s’agisse d’une seule photo ou d’un diaporama de 4 images, ces modèles ajoutent des animations fluides et du texte pour aider vos publications à toucher plus de monde.",
    edited: "Vous avez déjà filmé vos clips, mais vous ne voulez pas passer du temps au montage ou vous ne savez pas comment faire ? Importez vos clips dans l’un de ces modèles, choisissez le nombre et la durée des clips dont vous avez besoin, puis exportez la vidéo finale et partagez-la sur toutes vos plateformes en un clic.",
    marketing: "Attirez l’attention avec de courtes vidéos humoristiques mettant en scène un acteur dans des situations du quotidien. Parfait pour des publicités ou des publications qui ressemblent à de mini-histoires et font sourire votre audience.",
  },
  es: {
    social: "Convierte cualquier imagen estática en un video listo para publicar. Estas plantillas ya incluyen prompts de IA optimizados: solo sube tu imagen, haz clic en « Generar video » y luego edita o elimina el texto antes de exportar y compartir.",
    product: "En lugar de publicar una imagen, conviértela en una publicación de video atractiva. Ya sea una sola foto o una presentación de 4 imágenes, estas plantillas añaden animaciones suaves y texto para ayudar a que tus publicaciones lleguen a más personas.",
    edited: "¿Ya grabaste tus clips, pero no quieres dedicar tiempo a editar o no sabes cómo hacerlo? Sube tus clips a una de estas plantillas, elige el número y la duración de clips que necesitas, exporta el video final y compártelo en todas tus plataformas con un solo clic.",
    marketing: "Capta la atención con videos cortos y divertidos protagonizados por un actor en situaciones cotidianas. Ideales para anuncios o publicaciones que parecen mini historias y hacen sonreír a tu audiencia.",
  },
};

const FORMAT_FILTER_TRANSLATIONS = {
  en: {
    instagram: "Format: Instagram (4:5)",
    portrait: "Portrait (9:16)",
    square: "Square (1:1)",
  },
  fr: {
    instagram: "Format : Instagram (4:5)",
    portrait: "Portrait (9:16)",
    square: "Carré (1:1)",
  },
  es: {
    instagram: "Formato: Instagram (4:5)",
    portrait: "Vertical (9:16)",
    square: "Cuadrado (1:1)",
  },
};

const FORMAT_ORDER = ["instagram", "portrait", "square"];

const OTHER_VERSION_LABELS = {
  en: "Other variants",
  fr: "Autres variantes",
  es: "Otras variantes",
};

const SHOW_OTHER_TEMPLATES_LABELS = {
  en: "Show the other templates",
  fr: "Afficher les autres modèles",
  es: "Mostrar las otras plantillas",
};

const INITIAL_TEMPLATES_PER_CATEGORY = 3;


const TEMPLATE_REQUEST_NOTE = {
  en: {
    textBeforeLink: "Need a different template or a variation of an existing one? If you want another format (square, 9:16, etc.), a different duration, or specific edits, tell us what you need. When a request can benefit more users, we'll do our best to add it as a new template or variant.",
    linkLabel: "Contact us",
    textAfterLink: " and share your idea.",
  },
  fr: {
    textBeforeLink: "Vous avez besoin d’un modèle différent ou d’une variante d’un modèle existant ? Si vous souhaitez un autre format (carré, 9:16, etc.), une durée différente ou des modifications spécifiques, dites-nous ce qu’il vous faut. Lorsqu’une demande peut être utile à davantage d’utilisateurs, nous ferons de notre mieux pour l’ajouter comme nouveau modèle ou variante.",
    linkLabel: "Contactez-nous",
    textAfterLink: " et partagez votre idée.",
  },
  es: {
    textBeforeLink: "¿Necesitas una plantilla diferente o una variante de una plantilla existente? Si quieres otro formato (cuadrado, 9:16, etc.), una duración distinta o cambios específicos, cuéntanos lo que necesitas. Cuando una solicitud pueda beneficiar a más usuarios, haremos todo lo posible por añadirla como una nueva plantilla o variante.",
    linkLabel: "Contáctanos",
    textAfterLink: " y comparte tu idea.",
  },
};

const ThumbnailMedia = ({ template, isPlaying, setVideoRef }) => {
  const hasPreviewVideo = Boolean(template.preview_video_url);

  if (hasPreviewVideo) {
    return (
      <div className="relative w-full h-full">
        {template.thumbnail_url ? (
          <img
            src={template.thumbnail_url}
            alt={template.name}
            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-200 pointer-events-none ${isPlaying ? "opacity-0" : "opacity-100"}`}
          />
        ) : null}
        <video
          ref={(node) => setVideoRef(template.id, node)}
          src={template.preview_video_url}
          poster={template.thumbnail_url || undefined}
          muted
          playsInline
          preload="none"
          className={`w-full h-full object-cover transition-opacity duration-200 pointer-events-none ${isPlaying ? "opacity-100" : "opacity-0"}`}
          aria-label={`${template.name} preview video`}
        />
      </div>
    );
  }

  if (template.thumbnail_url) {
    return (
      <img
        src={template.thumbnail_url}
        alt={template.name}
        className="w-full h-full object-cover"
      />
    );
  }

  return (
    <div className="w-full h-full flex items-center justify-center">
      <Play className="w-12 h-12 text-violet-400" />
    </div>
  );
};

export default function TemplatesPage() {
  const { templates, fetchTemplates, isLoading } = useTemplateStore();
  const location = useLocation();
  const navigate = useLocalizedNavigation();
  const language = getPathLanguage(location.pathname);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [format, setFormat] = useState("instagram");
  const [viewMode, setViewMode] = useState("grid");
  const [desktopMode, setDesktopMode] = useState(true);
  const [hoveredTemplateId, setHoveredTemplateId] = useState(null);
  const [activeMobileTemplateId, setActiveMobileTemplateId] = useState(null);
  const [expandedTemplateIds, setExpandedTemplateIds] = useState(new Set());
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [languageMenuOpen, setLanguageMenuOpen] = useState(false);
  const videoRefs = useRef({});
  const cardRefs = useRef({});

  useEffect(() => {
    fetchTemplates(true, language);
  }, [fetchTemplates, language]);

  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
      return undefined;
    }

    const mediaQuery = window.matchMedia("(hover: hover) and (pointer: fine)");
    const updateDesktopMode = () => setDesktopMode(mediaQuery.matches);
    updateDesktopMode();

    if (typeof mediaQuery.addEventListener === "function") {
      mediaQuery.addEventListener("change", updateDesktopMode);
      return () => mediaQuery.removeEventListener("change", updateDesktopMode);
    }

    mediaQuery.addListener(updateDesktopMode);
    return () => mediaQuery.removeListener(updateDesktopMode);
  }, []);

  const filteredTemplates = useMemo(() => templates.filter((template) => {
    const matchesSearch = template.name.toLowerCase().includes(search.toLowerCase()) ||
      template.description?.toLowerCase().includes(search.toLowerCase());
    const isVisibleCategory = CATEGORY_ORDER.includes(template.category);
    const matchesCategory = category === "all" ? isVisibleCategory : template.category === category;

    const { canvas_width, canvas_height } = template;
    let matchesFormat = false;
    if (format === "portrait" && canvas_width * 16 === canvas_height * 9) matchesFormat = true;
    if (format === "square" && canvas_width === canvas_height) matchesFormat = true;
    if (format === "instagram" && canvas_width * 5 === canvas_height * 4) matchesFormat = true;

    return matchesSearch && matchesCategory && matchesFormat;
  }), [templates, search, category, format]);

  const visibleMainTemplates = useMemo(() => {
    const versionIds = new Set(
      templates.flatMap((template) => Array.isArray(template.other_versions) ? template.other_versions : [])
    );
    return filteredTemplates.filter((template) => !versionIds.has(template.id));
  }, [filteredTemplates, templates]);

  const templateById = useMemo(
    () => Object.fromEntries(filteredTemplates.map((template) => [template.id, template])),
    [filteredTemplates]
  );

  const categoryLabels = TEMPLATE_CATEGORY_TRANSLATIONS[language] || TEMPLATE_CATEGORY_TRANSLATIONS.en;
  const categoryDescriptions = CATEGORY_DESCRIPTIONS[language] || CATEGORY_DESCRIPTIONS.en;
  const formatLabels = FORMAT_FILTER_TRANSLATIONS[language] || FORMAT_FILTER_TRANSLATIONS.en;
  const categories = ["all", ...CATEGORY_ORDER].map((value) => ({
    value,
    label: categoryLabels[value] || TEMPLATE_CATEGORY_TRANSLATIONS.en[value],
  }));
  const formatFilters = FORMAT_ORDER.map((value) => ({
    value,
    label: formatLabels[value] || FORMAT_FILTER_TRANSLATIONS.en[value],
  }));

  const groupedTemplates = useMemo(() => CATEGORY_ORDER
    .map((value) => ({
      value,
      label: categoryLabels[value] || TEMPLATE_CATEGORY_TRANSLATIONS.en[value],
      templates: visibleMainTemplates.filter((template) => template.category === value),
    }))
    .filter((section) => section.templates.length > 0), [visibleMainTemplates, categoryLabels]);

  const switchLanguage = (nextLanguage) => {
    const basePath = stripLanguagePrefix(location.pathname);
    const localizedPath = withLanguagePrefix(basePath, nextLanguage);
    window.location.assign(`${localizedPath}${location.search}${location.hash}`);
    setLanguageMenuOpen(false);
  };

  const orderedTemplateIds = useMemo(() => groupedTemplates.flatMap((section) => {
    const displayedTemplates = expandedCategories.has(section.value)
      ? section.templates
      : section.templates.slice(0, INITIAL_TEMPLATES_PER_CATEGORY);

    return displayedTemplates.flatMap((template) => {
      const ids = [template.id];
      if (expandedTemplateIds.has(template.id)) {
        const otherVersionIds = (template.other_versions || []).filter((id) => templateById[id]);
        ids.push(...otherVersionIds);
      }
      return ids;
    });
  }), [groupedTemplates, expandedCategories, expandedTemplateIds, templateById]);

  const stopVideo = useCallback((templateId) => {
    const video = videoRefs.current[templateId];
    if (!video) {
      return;
    }

    video.pause();
    video.currentTime = 0;
  }, []);

  const playVideo = useCallback(async (templateId) => {
    const video = videoRefs.current[templateId];
    if (!video) {
      return;
    }

    video.currentTime = 0;

    try {
      await video.play();
    } catch (error) {
      console.debug("Template preview playback prevented", error);
    }
  }, []);

  const syncPlayback = useCallback((nextTemplateId) => {
    Object.entries(videoRefs.current).forEach(([templateId, video]) => {
      if (!video) {
        return;
      }

      if (templateId === nextTemplateId) {
        if (video.paused || video.ended) {
          playVideo(templateId);
        }
      } else {
        video.pause();
        video.currentTime = 0;
      }
    });
  }, [playVideo]);

  const refreshMobilePlayback = useCallback(() => {
    if (desktopMode || typeof window === "undefined") {
      return;
    }

    const nextVisibleTemplateId = orderedTemplateIds.find((templateId) => {
      const card = cardRefs.current[templateId];
      if (!card) {
        return false;
      }

      const rect = card.getBoundingClientRect();
      return rect.top >= 0 && rect.bottom <= window.innerHeight;
    }) || null;

    setActiveMobileTemplateId(nextVisibleTemplateId);
  }, [desktopMode, orderedTemplateIds]);

  useEffect(() => {
    if (desktopMode) {
      setActiveMobileTemplateId(null);
      return undefined;
    }

    refreshMobilePlayback();

    const observer = new IntersectionObserver(
      () => refreshMobilePlayback(),
      { threshold: [0, 0.25, 0.5, 0.75, 1] }
    );

    orderedTemplateIds.forEach((templateId) => {
      const card = cardRefs.current[templateId];
      if (card) {
        observer.observe(card);
      }
    });

    window.addEventListener("scroll", refreshMobilePlayback, { passive: true });
    window.addEventListener("resize", refreshMobilePlayback);

    return () => {
      observer.disconnect();
      window.removeEventListener("scroll", refreshMobilePlayback);
      window.removeEventListener("resize", refreshMobilePlayback);
    };
  }, [desktopMode, orderedTemplateIds, refreshMobilePlayback]);

  useEffect(() => {
    const targetTemplateId = desktopMode ? hoveredTemplateId : activeMobileTemplateId;
    syncPlayback(targetTemplateId);
  }, [desktopMode, hoveredTemplateId, activeMobileTemplateId, syncPlayback]);

  const handleUseTemplate = (templateId) => {
    navigate(`/editor/${templateId}`);
  };

  const setVideoRef = useCallback((templateId, node) => {
    if (node) {
      videoRefs.current[templateId] = node;
      node.onended = () => {
        node.pause();
        if (desktopMode) {
          const hasDuration = Number.isFinite(node.duration) && node.duration > 0;
          node.currentTime = hasDuration ? Math.max(0, node.duration - 0.05) : 0;
          return;
        }

        node.currentTime = 0;
      };
      return;
    }

    delete videoRefs.current[templateId];
  }, [desktopMode]);

  const setCardRef = useCallback((templateId, node) => {
    if (node) {
      cardRefs.current[templateId] = node;
      return;
    }

    delete cardRefs.current[templateId];
  }, []);

  const handleDesktopMouseEnter = useCallback((templateId) => {
    if (!desktopMode || !videoRefs.current[templateId]) {
      return;
    }

    setHoveredTemplateId(templateId);
  }, [desktopMode]);

  const handleDesktopMouseLeave = useCallback((templateId) => {
    if (!desktopMode || hoveredTemplateId !== templateId) {
      return;
    }

    setHoveredTemplateId(null);
    stopVideo(templateId);
  }, [desktopMode, hoveredTemplateId, stopVideo]);

  const getFormatLabel = (width, height) => {
    if (width === height) return "Square";
    if (width < height) return "Portrait";
    return "Landscape";
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />

      <main className="pt-24 pb-16 px-4 md:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="mb-8">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Video Templates</h1>
            <p className="text-lg text-slate-600">
              Choose from professionally designed templates and make them your own
            </p>
          </div>

          <div className="flex flex-col md:flex-row gap-4 mb-8">
            <div className="relative md:w-64 lg:w-72 md:flex-none">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Search templates..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 h-12 bg-white border-slate-200"
                data-testid="template-search"
              />
            </div>

            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="w-full md:w-80 lg:w-96 h-12 bg-white text-left [&>span]:flex-1 [&>span]:text-left" data-testid="category-filter">
                <Filter className="w-4 h-4 mr-2 text-slate-400" />
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((cat) => (
                  <SelectItem key={cat.value} value={cat.value}>
                    {cat.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={format} onValueChange={setFormat}>
              <SelectTrigger className="w-full md:w-64 h-12 bg-white text-left [&>span]:flex-1 [&>span]:text-left" data-testid="format-filter">
                <SelectValue placeholder="Format" />
              </SelectTrigger>
              <SelectContent>
                {formatFilters.map((f) => (
                  <SelectItem key={f.value} value={f.value}>
                    {f.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <div className="flex gap-2">
              <Button
                variant={viewMode === "grid" ? "default" : "outline"}
                size="icon"
                onClick={() => setViewMode("grid")}
                className="h-12 w-12"
              >
                <Grid3X3 className="w-5 h-5" />
              </Button>
              <Button
                variant={viewMode === "list" ? "default" : "outline"}
                size="icon"
                onClick={() => setViewMode("list")}
                className="h-12 w-12"
              >
                <LayoutList className="w-5 h-5" />
              </Button>
            </div>
          </div>

          {isLoading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-white rounded-2xl h-80 animate-pulse" />
              ))}
            </div>
          ) : visibleMainTemplates.length === 0 ? (
            <div className="text-center py-16">
              <div className="w-16 h-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                <Search className="w-8 h-8 text-slate-400" />
              </div>
              <h3 className="text-xl font-semibold mb-2">No templates found</h3>
              <p className="text-slate-600">Try adjusting your search or filters</p>
            </div>
          ) : (
            <div className="space-y-10">
              {groupedTemplates.map((section) => {
                const isCategoryExpanded = expandedCategories.has(section.value);
                const displayedTemplates = isCategoryExpanded
                  ? section.templates
                  : section.templates.slice(0, INITIAL_TEMPLATES_PER_CATEGORY);
                const hasHiddenTemplates = section.templates.length > INITIAL_TEMPLATES_PER_CATEGORY;

                return (
                  <section key={section.value}>
                    <h2 className="text-2xl md:text-3xl font-bold mb-2">{section.label}</h2>
                    <p className="text-slate-600 mb-5 max-w-4xl">{categoryDescriptions[section.value]}</p>
                    <div className={viewMode === "grid" ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" : "flex flex-col gap-4"}>
                    {displayedTemplates.map((template) => {
                      const isPlaying = desktopMode
                        ? hoveredTemplateId === template.id
                        : activeMobileTemplateId === template.id;
                      const otherVersionTemplates = (template.other_versions || [])
                        .map((id) => templateById[id])
                        .filter(Boolean);
                      const hasOtherVersions = otherVersionTemplates.length > 0;
                      const isExpanded = expandedTemplateIds.has(template.id);

                      return (
                        <Fragment key={template.id}>
                          <div
                            ref={(node) => setCardRef(template.id, node)}
                            className={`group bg-white rounded-2xl border border-slate-200 overflow-hidden hover:shadow-md transition-all duration-300 cursor-pointer ${
                              viewMode === "list" ? "flex" : "hover:-translate-y-1"
                            }`}
                            data-testid={`template-card-${template.id}`}
                            onClick={() => handleUseTemplate(template.id)}
                            onKeyDown={(event) => {
                              if (event.key === "Enter" || event.key === " ") {
                                event.preventDefault();
                                handleUseTemplate(template.id);
                              }
                            }}
                            role="button"
                            tabIndex={0}
                            onMouseEnter={() => handleDesktopMouseEnter(template.id)}
                            onMouseLeave={() => handleDesktopMouseLeave(template.id)}
                          >
                            <div className={`relative overflow-hidden ${viewMode === "list" ? "w-48 flex-shrink-0" : "aspect-[4/5]"} bg-gradient-to-br from-violet-100 to-sky-100`}>
                              <ThumbnailMedia
                                template={template}
                                isPlaying={isPlaying}
                                setVideoRef={setVideoRef}
                              />

                              <div className="absolute top-3 left-3 flex gap-2 pointer-events-none">
                                {template.layers?.some((layer) => layer.pixverse_prompt) && (
                                  <span className="px-2 py-1 rounded-full bg-gradient-to-r from-teal-600 to-cyan-500 text-white text-xs font-medium flex items-center gap-1">
                                    <Sparkles className="w-3 h-3" />
                                    AI
                                  </span>
                                )}
                              </div>
                            </div>

                            <div className={`p-4 ${viewMode === "list" ? "flex-1" : ""}`}>
                              <h3 className="font-semibold text-lg mb-1 group-hover:text-teal-600 transition-colors">
                                {template.name}
                              </h3>
                              {template.description && (
                                <p className="text-slate-600 text-sm mb-3">{template.description}</p>
                              )}
                              <div className="flex items-center gap-4 text-sm text-slate-500 flex-wrap">
                                <span className="flex items-center gap-1">
                                  {getFormatLabel(template.canvas_width, template.canvas_height)}
                                </span>
                                <span className="flex items-center gap-1">
                                  <Clock className="w-4 h-4" />
                                  {template.duration}s
                                </span>
                                <span>
                                  {template.canvas_width}×{template.canvas_height}
                                </span>
                                {hasOtherVersions && (
                                  <button
                                    type="button"
                                    className="inline-flex items-center gap-2 px-4 py-2 rounded-[14px] bg-teal-600 hover:bg-teal-700 text-white text-lg font-medium transition-colors"
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      setExpandedTemplateIds((prev) => {
                                        const next = new Set(prev);
                                        if (next.has(template.id)) {
                                          next.delete(template.id);
                                        } else {
                                          next.add(template.id);
                                        }
                                        return next;
                                      });
                                    }}
                                  >
                                    {OTHER_VERSION_LABELS[language] || OTHER_VERSION_LABELS.en} ({otherVersionTemplates.length})
                                    {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                                  </button>
                                )}
                              </div>
                            </div>
                          </div>
                          {hasOtherVersions && isExpanded && (
                            <div className={`col-span-full ${viewMode === "list" ? "ml-4" : ""}`}>
                              <div className={viewMode === "grid" ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-2" : "flex flex-col gap-4"}>
                                {otherVersionTemplates.map((versionTemplate) => {
                                  const isVersionPlaying = desktopMode
                                    ? hoveredTemplateId === versionTemplate.id
                                    : activeMobileTemplateId === versionTemplate.id;

                                  return (
                                    <div
                                      key={versionTemplate.id}
                                      ref={(node) => setCardRef(versionTemplate.id, node)}
                                      className={`group bg-white rounded-2xl border border-teal-100 overflow-hidden hover:shadow-md transition-all duration-300 cursor-pointer ${
                                        viewMode === "list" ? "flex" : "hover:-translate-y-1"
                                      }`}
                                      onClick={() => handleUseTemplate(versionTemplate.id)}
                                      onMouseEnter={() => handleDesktopMouseEnter(versionTemplate.id)}
                                      onMouseLeave={() => handleDesktopMouseLeave(versionTemplate.id)}
                                    >
                                      <div className={`relative overflow-hidden ${viewMode === "list" ? "w-48 flex-shrink-0" : "aspect-[4/5]"} bg-gradient-to-br from-violet-100 to-sky-100`}>
                                        <ThumbnailMedia
                                          template={versionTemplate}
                                          isPlaying={isVersionPlaying}
                                          setVideoRef={setVideoRef}
                                        />
                                      </div>
                                      <div className={`p-4 ${viewMode === "list" ? "flex-1" : ""}`}>
                                        <h3 className="font-semibold text-lg mb-1 group-hover:text-teal-600 transition-colors">
                                          {versionTemplate.name}
                                        </h3>
                                        {versionTemplate.description && (
                                          <p className="text-slate-600 text-sm mb-3">{versionTemplate.description}</p>
                                        )}
                                        <div className="flex items-center gap-4 text-sm text-slate-500 flex-wrap">
                                          <span>{getFormatLabel(versionTemplate.canvas_width, versionTemplate.canvas_height)}</span>
                                          <span className="flex items-center gap-1">
                                            <Clock className="w-4 h-4" />
                                            {versionTemplate.duration}s
                                          </span>
                                          <span>
                                            {versionTemplate.canvas_width}×{versionTemplate.canvas_height}
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>
                          )}
                        </Fragment>
                      );
                    })}
                    {hasHiddenTemplates && !isCategoryExpanded && (
                      <div className="col-span-full flex justify-center">
                        <Button
                          type="button"
                          className="btn-ai h-auto inline-flex items-center gap-2 px-4 py-2 rounded-[14px] text-lg font-medium transition-colors"
                          data-testid={`show-all-templates-${section.value}`}
                          onClick={() => setExpandedCategories((previousCategories) => {
                            const nextCategories = new Set(previousCategories);
                            nextCategories.add(section.value);
                            return nextCategories;
                          })}
                        >
                          {SHOW_OTHER_TEMPLATES_LABELS[language] || SHOW_OTHER_TEMPLATES_LABELS.en} ({section.templates.length - INITIAL_TEMPLATES_PER_CATEGORY})
                          <ChevronDown className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                    </div>
                  </section>
                );
              })}
            </div>
          )}

          <div className="mt-12 rounded-2xl border border-slate-200 bg-white p-5 md:p-6">
            <p className="text-sm md:text-base text-slate-700 leading-relaxed">
              {(TEMPLATE_REQUEST_NOTE[language] || TEMPLATE_REQUEST_NOTE.en).textBeforeLink}{" "}
              <a href="mailto:support@creavideos.online" className="text-teal-600 font-medium hover:text-teal-700 underline underline-offset-2">
                {(TEMPLATE_REQUEST_NOTE[language] || TEMPLATE_REQUEST_NOTE.en).linkLabel}
              </a>
              {(TEMPLATE_REQUEST_NOTE[language] || TEMPLATE_REQUEST_NOTE.en).textAfterLink}
            </p>
          </div>
        </div>
      </main>

      <footer className="py-12 px-4 md:px-8 bg-slate-900 text-slate-400">
        <div className="max-w-7xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-2">
              <img
                src="/assets/logo-creavideos.png"
                alt="CreaVideos"
                className="h-8 w-auto opacity-80"
              />
            </div>
            <div className="flex flex-wrap gap-6 text-sm">
              <LocalizedLink to="/" className="hover:text-white transition-colors">Homepage</LocalizedLink>
              <LocalizedLink to="/pricing" className="hover:text-white transition-colors">Pricing</LocalizedLink>
              <LocalizedLink to="/terms-of-service" className="hover:text-white transition-colors">Terms</LocalizedLink>
              <LocalizedLink to="/license-agreement" className="hover:text-white transition-colors">License Agreement</LocalizedLink>
              <LocalizedLink to="/privacy-policy" className="hover:text-white transition-colors">Privacy</LocalizedLink>
              <LocalizedLink to="/privacy-policy#data-deletion-instructions" className="hover:text-white transition-colors">Data Deletion</LocalizedLink>
              <a href="mailto:support@creavideos.online" className="hover:text-white transition-colors">Support</a>
              <div className="relative">
                <button
                  type="button"
                  className="hover:text-white transition-colors"
                  onClick={() => setLanguageMenuOpen((prev) => !prev)}
                >
                  Language
                </button>
                {languageMenuOpen && (
                  <div className="absolute right-0 bottom-full mb-2 min-w-36 rounded-lg border border-slate-700 bg-slate-950/95 p-2 shadow-lg">
                    {SUPPORTED_LANGUAGES.map((languageCode) => (
                      <button
                        key={languageCode}
                        type="button"
                        className={`block w-full rounded px-3 py-2 text-left text-sm transition-colors ${
                          language === languageCode
                            ? "bg-slate-800 text-white"
                            : "text-slate-300 hover:bg-slate-800 hover:text-white"
                        }`}
                        onClick={() => switchLanguage(languageCode)}
                      >
                        {LANGUAGE_NAMES[languageCode]}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-slate-800 text-center text-sm">
            © 2025 CreaVideos. All rights reserved.
          </div>
        </div>
      </footer>
    </div>
  );
}
